import { ChangeDetectorRef, Component, Injector, OnInit } from '@angular/core';
import { TransactionsService } from '@services/entity/transactions.service';
import { Chart, registerables } from 'chart.js';
import '@angular/localize/init';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { ComponentBase } from '@abstracts/component-base';
import { TransactionFilter } from '@models/transactionFilter';
import { ActivatedRoute } from '@angular/router';
import { endOfISOWeek, endOfMonth, startOfISOWeek, startOfMonth } from 'date-fns';
import { TransactionDto } from '@dto/transactionDto';
import { plainToClass } from 'class-transformer';

Chart.register(...registerables);

@Component({
  selector: 'app-dashboard-power-chart',
  templateUrl: './dashboard-power-chart.component.html',
  styleUrls: ['./dashboard-power-chart.component.scss']
})
export class DashboardPowerChartComponent extends ComponentBase implements OnInit {

  public className = 'DashboardPowerChartComponent';

  public chart: Chart;
  public canvas: any;
  public ctx: any;
  public xAxisLabel: string;
  public labels: string[];
  public barChartData: number[];
  public totalAmount: number;
  public kwText = $localize`:@@KwText:Lademenge in kWh`
  public loading: boolean = true;
  public chartGradientA: string = '#f57e00';
  public chartGradientB: string = '#ffbf00';
  public transactions: TransactionDto[]
  public customerId?: number

  public date: Date;

  public isOperator: boolean = false;
  public isUser: boolean = false;

  constructor(
    private transactionsService: TransactionsService,
    private activatedRoute: ActivatedRoute,
    private ref: ChangeDetectorRef,
    injector: Injector) {
    super(injector);
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngAfterViewInit() {
    this.getChartColor()
    this.setDefaultValues();

    if (this.authenticationService.userHasRole("Operator")) {
      this.isOperator = true;
      this.customerId = this.activatedRoute.parent.snapshot.params.key
    }
    if (this.authenticationService.userHasRole("User")) {
      this.isUser = true;
    }

    this.loadBarChartDataWeekly();
  }

  getChartColor() {
    var style = window.getComputedStyle(document.body)
    const chartGradientA = style.getPropertyValue('--chart-gradient-a')
    const chartGradientB = style.getPropertyValue('--chart-gradient-b')

    if (chartGradientA && chartGradientB) {
      this.chartGradientA = chartGradientA
      this.chartGradientB = chartGradientB
    }
  }

  setDefaultValues() {
    this.date = new Date();
  }

  private loadBarChartDataWeekly() {
    var transactionFilter: TransactionFilter = {
      from: startOfISOWeek(this.date).toISOString(),
      to: endOfISOWeek(this.date).toISOString(),
      customerId: this.customerId
    };
    this.loadTransactions(transactionFilter)
  }

  private loadBarChartDataMonthly() {
    var transactionFilter: TransactionFilter = {
      from: startOfMonth(this.date).toISOString(),
      to: endOfMonth(this.date).toISOString(),
      customerId: this.customerId
    }
    this.loadTransactions(transactionFilter)
  }

  loadTransactions(transactionFilter) {
    this.transactionsService.GetPowerConsumptionCustom(transactionFilter).subscribe(result => {
      this.transactions = plainToClass(TransactionDto, result);
      this.calculateData(transactionFilter)
    }, error => { })
  }

  calculateData(filter) {
    this.loading = true;
    const powerData = [];
    const labels = [];
    const from = new Date(filter.from);
    const to = new Date(filter.to);

    const numberOfIntervals = Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));

    for (let i = 0; i <= (numberOfIntervals - 1); i++) {
      const currentDate = new Date(from);
      currentDate.setDate(from.getDate() + i);
      labels.push(currentDate.getDate());
      const nextInterval = new Date(currentDate);
      nextInterval.setDate(currentDate.getDate() + 1);
      const consumptionOfInterval = this.transactions
        .filter(t => new Date(t.startTimestamp) >= currentDate && new Date(t.startTimestamp) < nextInterval)
        .reduce((sum, t) => sum + t.meterAmount, 0);

      powerData.push(consumptionOfInterval / 1000.0);
    }

    this.setupChart(labels, powerData);
  }

  setupChart(labels: string[], powerData: number[]) {
    this.barChartData = powerData;
    this.labels = labels;
    this.xAxisLabel = 'Tag';
    this.totalAmount = this.barChartData.reduce((accumulator, current) => { return accumulator + current; }, 0)
    this.createChart(this.kwText);
  }

  public changeChart(type) {
    this.chart.destroy();
    this.loading = true;
    this.ref.detectChanges();

    switch (type) {
      case 'week':
        this.loadBarChartDataWeekly();
        break;
      case 'month':
        this.loadBarChartDataMonthly();
        break;
    }
  }

  private createChart(label: string) {
    this.canvas = document.getElementById('powerChartDashboard');
    this.ctx = this.canvas.getContext('2d');
    var background_2 = this.ctx.createLinearGradient(0, 0, 0, 600);
    background_2.addColorStop(0, this.chartGradientA);
    background_2.addColorStop(1, this.chartGradientB);

    this.chart = new Chart(this.ctx, {
      plugins: [ChartDataLabels],
      type: 'bar',
      data: {
        labels: this.labels,
        datasets: [{
          label: label,
          data: this.barChartData,
          backgroundColor: background_2,
          borderColor: '#f57e00',
          borderWidth: 0,
          barPercentage: 1,
          xAxisID: 'x',
          yAxisID: 'y'
        },
        {
          label: 'Gesamtlademenge in kWh: ' + this.totalAmount.toLocaleString(),
          data: null,
          backgroundColor: 'white',
          xAxisID: 'none',
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          'y': {
            beginAtZero: true,
            title: { display: true, text: 'Lademenge in kWh' },
          },
          'x': {
            title: { display: true, text: this.xAxisLabel },
          },
          'none': {
            display: false
          }
        },
        plugins: {
          datalabels: {
            anchor: 'center',
            align: 'center',
            labels: {
              value: {
                color: 'white'
              }
            },
            display: function (context) {
              return null //context.dataset.data[context.dataIndex] !== 0;
            }
          },
          legend: {
            title: {
              display: true,
            },
            onClick: null
          },
        }
      }
    });
    this.loading = false;
  }

}
